<template>
  <div ref="activityCardContainerRef" class="activity-card-container" :data-activity-id="activityNo">
    <!-- 활동 카드 -->
    <div class="activity-card" :class="{ 'activity-card--my-post': userInfo?.id === createUserId }">
      <!-- 활동 헤더 -->
      <div class="activity-header">
        <div class="activity-user-section">
          <!-- 사용자 정보 (피드백과 동일 한 줄 레이아웃) -->
          <div class="collaboration-item__user-row collaboration-item__user-row--single">
            <UserImg :user-id="createUserId" :user-name="createUserNm" />
            <div class="user-info-lines user-info-lines--single">
              <div class="user-line-1">
                <ActivityUserHoverTooltip :user-id="createUserId" :user-name="createUserNm" :user-dept="createUserDept">
                  <span class="user-name">{{ createUserNm }}</span>
                </ActivityUserHoverTooltip>
                <span class="user-dot">·</span>
                <span class="user-line-1-meta">
                  <ActivityDateHoverTooltip :created-date="createdDate" :activity-date="activityDt">
                    <span class="collaboration-item__date">{{ activityDt }}</span>
                  </ActivityDateHoverTooltip>
                  <span class="user-dot">·</span>
                  <UiTooltip v-if="evaluation" position="bottom">
                    <template #trigger>
                      <span class="task-item__tag" :style="'background-color:' + getTaskStatusColor(statusColor)">
                        {{ resultStatusNm }}
                      </span>
                    </template>
                    <p>{{ evaluation }}</p>
                  </UiTooltip>
                  <span
                    v-else
                    class="task-item__tag"
                    :style="'background-color:' + getTaskStatusColor(statusColor)"
                  >
                    {{ resultStatusNm }}
                  </span>
                  <template v-if="showReadStatus && (isReadUserCntVisible || activityUnreadCnt > 0)">
                    <span v-if="isReadUserCntVisible" class="user-dot">·</span>
                    <ReadStatusIndicator
                      :readUserCnt="readUserCnt"
                      :readUserStr="readUserStr"
                      :unreadCnt="activityUnreadCnt"
                      @new-click="handleActivityNewClick"
                    />
                  </template>
                </span>
              </div>
            </div>
          </div>

          <div class="activity-actions">
            <UiTooltip position="bottom-right" v-if="atchFileId && atchFileCnt > 0">
              <template #trigger>
                <UiButton
                  iconOnly
                  variant="ghost"
                  size="small"
                  class="attachment-btn icon-hover-filter"
                  @click="openFileAttachmentModal_detailActivity"
                >
                  <i class="icon icon-paper-clip icon-md"></i>
                  {{ atchFileCnt }}
                </UiButton>
              </template>
              <p>{{ t('common.activityCard.attachment') }}</p>
            </UiTooltip>
            <p v-if="hasIbmsActivityId" class="ibms-activity-notice">{{ t('common.activityCard.ibmsActivityNotice') }}</p>
            <template v-else-if="(!activityReadOnly || showActivityEditDelete) && userInfo?.id === createUserId">
              <UiTooltip position="bottom-right">
                <template #trigger>
                  <UiButton iconOnly variant="ghost" size="small" class="icon-hover-filter" @click="startEdit">
                    <i class="icon icon-pencil icon-lg"></i>
                  </UiButton>
                </template>
                <p>{{ t('common.action.edit') }}</p>
              </UiTooltip>
              <UiTooltip position="bottom-right">
                <template #trigger>
                  <UiButton iconOnly variant="ghost" size="small" class="icon-hover-filter" @click="handleActivityDelete">
                    <i class="icon icon-delete icon-lg"></i>
                  </UiButton>
                </template>
                <p>{{ t('common.action.delete') }}</p>
              </UiTooltip>
            </template>
            <ActivityFeedbackCommentBtn
              v-if="!props.feedbackReadOnly && !isEditing"
              :count="replyCnt"
              :activity-id="String(activityNo)"
              :tooltip="t('common.activityCard.writeFeedback')"
              @click="showAddCommentModal"
            />
          </div>
        </div>
      </div>
      <!-- 활동 헤더 EEE -->

      <!-- 활동 내용 -->
      <div v-if="isEditing" class="editor-container edit-activity-section-highlighted">
        <!-- 에디터 상단 컨트롤 -->
        <div class="editor-top-controls flex justify-between mb-16 items-center">
          <div class="left-controls flex gap-2 items-center">
            <!-- ui-date-picker 컴포넌트 -->
            <div class="date-picker-wrapper">
              <span class="control-label">{{ t('common.activityCard.dateLabel') }}</span>
              <UiDatePicker class="date-picker" :start-placeholder="t('common.datePicker.startDate')" :end-placeholder="t('common.activityCard.endDatePlaceholder')" v-model="editCurrentDate" />
            </div>
          </div>
          <div class="right-controls flex gap-2 items-center">
            <div class="select-group flex gap-2">
              <div class="select-wrapper">
                <UiSelect class="min-w-130" :options="localizedResultStatusCdOptions" v-model="editStatus" />
              </div>
              <div class="select-wrapper">
                <UiSelect class="min-w-130" :options="statusCdOptions" v-model="editEvaluation" />
              </div>
            </div>
          </div>
        </div>

        <!-- QuillEditor -->
        <TinymceEditor v-model="editedContent" :minHeight="200" :maxHeight="400" :resizable="editorResizable" :placeholder="t('common.activityCard.activityContentPlaceholder')" />

        <!-- 에디터 하단 컨트롤 -->
        <div class="editor-bottom-actions flex justify-between mt-20 items-center">
          <div class="left-actions">
            <UiTooltip position="bottom">
              <template #trigger>
                <UiButton iconOnly variant="ghost" size="small" @click="openFileAttachmentModal_editActivity">
                  <i class="icon icon-paper-clip icon-md"></i>
                  {{ editActivityAttachmentCount }}
                </UiButton>
              </template>
              <p>{{ t('common.activityCard.attachFile') }}</p>
            </UiTooltip>
          </div>
          <div class="right-actions flex gap-2">
            <UiButton variant="secondary-line" @click="cancelEdit">{{ t('common.action.cancel') }}</UiButton>
            <UiButton variant="primary" @click="saveEdit" :disabled="isEditorEmpty(editedContent)">{{ t('common.action.save') }}</UiButton>
          </div>
        </div>
      </div>
      <div v-else class="activity-feed-body">
        <TinymceViewer :content="props.content" class="activity-list" zero-body-margin />
        <ActivityPhotoGallery :thumb-list="props.thumbPaths" :origin-list="props.imgPaths" />
      </div>
      <!-- 활동 내용 EEE -->
    </div>

    <!-- 피드백 추가 모달 -->
    <div class="comment-section comment-section--highlighted" v-if="addComment" ref="addCommentSectionRef">
      <div class="comment-container">
        <div class="comment-card comment-card--highlighted">
          <div class="comment-content">
            <TinymceEditor v-model="addFeedbackContent" :minHeight="150" :maxHeight="350" :resizable="editorResizable" :placeholder="t('common.activityCard.feedbackContentPlaceholder')" />
            <div class="flex justify-between items-center comment-compose-footer">
              <button type="button" class="attachment-btn action-btn" @click="openFeedbackAttachment('addFeedback')">
                <Icon name="heroicons:paper-clip" size="20" />
                {{ attachmentFiles_addFeedback.length }}
              </button>
              <div class="flex gap-2">
                <UiButton variant="secondary-line" @click="cancelAddComment">{{ t('common.action.cancel') }}</UiButton>
                <UiButton variant="primary" @click="saveAddComment" :disabled="isSavingAddComment || isEditorEmpty(addFeedbackContent)">{{ t('common.action.save') }}</UiButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 피드백 섹션 -->
    <div
      class="comment-section"
      v-if="feedbackList.length > 0"
      v-for="feedback in feedbackList"
      :key="feedback.id"
      :data-activity-id="activityNo + '_' + feedback.replyNo"
    >
      <div class="comment-container">
        <div class="comment-card" :class="{ 'comment-card--my-comment': userInfo?.id === feedback.createUserId }">
          <!-- 사용자 정보 -->
          <div class="comment-card--header collaboration-item__user">
            <div class="user-info">
              <UserImg :user-id="feedback.createUserId" :user-name="feedback.createUserNm" />
              <div class="user-info-lines user-info-lines--single">
                <div class="user-line-1">
                  <ActivityUserHoverTooltip :user-id="feedback.createUserId" :user-name="feedback.createUserNm" :user-dept="feedback.createUserDept">
                    <span class="user-name">{{ feedback.createUserNm }}</span>
                  </ActivityUserHoverTooltip>
                  <div class="user-dot">·</div>
                  <span class="user-line-1-meta">
                    <span class="collaboration-item__date">{{ feedback.createdDate }}</span>
                    <template v-if="showReadStatus && (isReadUserCntVisible || feedback.activityUnreadCnt > 0)">
                      <div v-if="isReadUserCntVisible" class="user-dot">·</div>
                      <ReadStatusIndicator
                        :readUserCnt="feedback.readUserCnt"
                        :readUserStr="feedback.readUserStr"
                        :unreadCnt="feedback.activityUnreadCnt"
                        @new-click="handleFeedbackNewClick(feedback)"
                      />
                    </template>
                  </span>
                </div>
              </div>
            </div>
            <div class="comment-actions">
              <UiTooltip position="bottom-right" v-if="feedback.atchFileId && Number(feedback.atchFileCnt) > 0">
                <template #trigger>
                  <UiButton
                    iconOnly
                    variant="ghost"
                    size="small"
                    class="attachment-btn icon-hover-filter"
                    @click="openCommentAttachmentView(feedback.atchFileId)"
                  >
                    <i class="icon icon-paper-clip icon-md"></i>
                    {{ feedback.atchFileCnt }}
                  </UiButton>
                </template>
                <p>{{ t('common.activityCard.attachment') }}</p>
              </UiTooltip>
              <template v-if="!feedbackReadOnly && userInfo?.id === feedback.createUserId">
                <UiTooltip position="bottom-right">
                  <template #trigger>
                    <UiButton iconOnly variant="ghost" size="small" @click="handleStartEditComment(feedback)">
                      <i class="icon icon-pencil icon-sm"></i>
                    </UiButton>
                  </template>
                  <p>{{ t('common.activityCard.editFeedback') }}</p>
                </UiTooltip>
                <UiTooltip position="bottom-right">
                  <template #trigger>
                    <UiButton iconOnly variant="ghost" size="small" @click="handleDeleteComment(feedback)">
                      <i class="icon icon-delete icon-sm"></i>
                    </UiButton>
                  </template>
                  <p>{{ t('common.activityCard.deleteFeedback') }}</p>
                </UiTooltip>
              </template>
              <ActivityFeedbackCommentBtn
                v-if="!feedbackReadOnly && !editingFeedbackIds.has(feedback.replyNo)"
                :count="feedback.replyCnt"
                :activity-id="`${activityNo}_${feedback.replyNo}`"
                :tooltip="t('common.activityCard.replyComment')"
                @click="handleStartReply(feedback)"
              />
            </div>
          </div>
          <template v-if="!editingFeedbackIds.has(feedback.replyNo)">
            <div class="activity-feed-body">
              <TinymceViewer :content="feedback.content" class="comment-content" zero-body-margin />
            </div>
          </template>
          <div v-else class="edit-comment-section edit-comment-section--highlighted">
            <TinymceEditor
              v-model="editingFeedbackContents[feedback.replyNo]"
              :minHeight="150"
              :maxHeight="350"
              :resizable="editorResizable"
              :placeholder="t('common.activityCard.feedbackContentPlaceholder')"
            />
            <div class="flex justify-between items-center comment-compose-footer">
              <button
                type="button"
                class="attachment-btn action-btn"
                @click="openFeedbackAttachment('editFeedback', feedback.replyNo, feedback.atchFileId)"
              >
                <Icon name="heroicons:paper-clip" size="20" />
                {{ getEditAttachmentCount('editFeedback', feedback.replyNo) }}
              </button>
              <div class="flex gap-2">
                <UiButton variant="secondary-line" @click="cancelEditComment(feedback)">{{ t('common.action.cancel') }}</UiButton>
                <UiButton variant="primary" @click="saveEditComment(feedback)" :disabled="isSavingEditComment.has(feedback.replyNo) || isEditorEmpty(editingFeedbackContents[feedback.replyNo])">{{ t('common.action.save') }}</UiButton>
              </div>
            </div>
          </div>
        </div>

        <!-- 피드백 답글 추가 섹션 SS -->
        <div v-if="addReply.has(feedback.replyNo)" :ref="el => (replySectionRefs[feedback.replyNo] = el)" class="reply-section--highlighted">
          <div class="comment-container comment-container--nested">
            <div class="comment-card comment-card--my-comment comment-card--highlighted">
              <!-- 추가 댓글 내용 -->
              <div class="comment-content">
                <TinymceEditor
                  v-model="addReplyContent[feedback.replyNo]"
                  :minHeight="150"
                  :maxHeight="350"
                  :resizable="editorResizable"
                  :placeholder="t('common.activityCard.replyToFeedbackPlaceholder')"
                />
              </div>
              <div class="flex justify-between items-center comment-compose-footer">
                <button type="button" class="attachment-btn action-btn" @click="openFeedbackAttachment('addReply', feedback.replyNo)">
                  <Icon name="heroicons:paper-clip" size="20" />
                  {{ getAttachmentBucket('addReply', feedback.replyNo).length }}
                </button>
                <div class="flex gap-2">
                  <UiButton variant="secondary-line" @click="addReply.delete(feedback.replyNo)">{{ t('common.action.cancel') }}</UiButton>
                  <UiButton variant="primary" @click="saveAddReply(feedback)" :disabled="isSavingAddReply.has(feedback.replyNo) || isEditorEmpty(addReplyContent[feedback.replyNo])">{{ t('common.action.save') }}</UiButton>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- 피드백 답글 추가 섹션 EE -->

        <!-- 피드백 댓글 섹션 SS -->
        <div v-for="reply in feedback.feedbackReplyList" :key="reply.id" :data-activity-id="activityNo + '_' + reply.replyNo">
          <div class="comment-container">
            <div class="comment-card" :class="{ 'comment-card--my-comment': userInfo?.id === reply.createUserId }">
              <!-- 사용자 정보 (1차 피드백과 동일 2줄 레이아웃) -->
              <div class="comment-card--header collaboration-item__user">
                <div class="user-info">
                  <UserImg :user-id="reply.createUserId" :user-name="reply.createUserNm" />
                  <div class="user-info-lines user-info-lines--single">
                    <div class="user-line-1">
                      <ActivityUserHoverTooltip :user-id="reply.createUserId" :user-name="reply.createUserNm" :user-dept="reply.createUserDept">
                        <span class="user-name">{{ reply.createUserNm }}</span>
                      </ActivityUserHoverTooltip>
                      <div class="user-dot">·</div>
                      <span class="user-line-1-meta">
                        <span class="collaboration-item__date">{{ reply.createdDate }}</span>
                        <template v-if="showReadStatus && (isReadUserCntVisible || reply.activityUnreadCnt > 0)">
                          <div v-if="isReadUserCntVisible" class="user-dot">·</div>
                          <ReadStatusIndicator
                            :readUserCnt="reply.readUserCnt"
                            :readUserStr="reply.readUserStr"
                            :unreadCnt="reply.activityUnreadCnt"
                            @new-click="handleReplyNewClick(reply)"
                          />
                        </template>
                      </span>
                    </div>
                  </div>
                </div>

                <div class="comment-actions">
                  <UiTooltip position="bottom-right" v-if="reply.atchFileId && Number(reply.atchFileCnt) > 0">
                    <template #trigger>
                      <UiButton
                        iconOnly
                        variant="ghost"
                        size="small"
                        class="attachment-btn icon-hover-filter"
                        @click="openCommentAttachmentView(reply.atchFileId)"
                      >
                        <i class="icon icon-paper-clip icon-md"></i>
                        {{ reply.atchFileCnt }}
                      </UiButton>
                    </template>
                    <p>{{ t('common.activityCard.attachment') }}</p>
                  </UiTooltip>
                  <template v-if="!feedbackReadOnly && userInfo?.id === reply.createUserId">
                    <UiTooltip position="bottom-right">
                      <template #trigger>
                        <UiButton iconOnly variant="ghost" size="small" @click="handleStartEditReply(reply)">
                          <i class="icon icon-pencil icon-sm"></i>
                        </UiButton>
                      </template>
                      <p>{{ t('common.activityCard.editReply') }}</p>
                    </UiTooltip>
                    <UiTooltip position="bottom-right">
                      <template #trigger>
                        <UiButton iconOnly variant="ghost" size="small" @click="handleDeleteReply(reply)">
                          <i class="icon icon-delete icon-sm"></i>
                        </UiButton>
                      </template>
                      <p>{{ t('common.activityCard.deleteReply') }}</p>
                    </UiTooltip>
                  </template>
                  <ActivityFeedbackCommentBtn
                    v-if="!feedbackReadOnly && !editingReplyIds.has(reply.replyNo)"
                    :count="reply.replyCnt"
                    :activity-id="`${activityNo}_${reply.replyNo}`"
                    :tooltip="t('common.activityCard.replyComment')"
                    @click="handleStartNestedReply(reply)"
                  />
                </div>
              </div>
              <div
                class="comment-reference comment-reference--reply"
                :class="{ 'is-show': isReplyReferenceExpanded(reply.replyNo) }"
                :data-activity-id="activityNo + '_' + reply.replyNo"
                :data-up-activity-id="activityNo + '_' + reply.upReplyNo"
              >
                <div class="reference-content">
                  <!-- 작성자 알약(좌) + 펼치기/접기(우) — 박스 우측 상단 -->
                  <div class="reference-content__head">
                    <ActivityUserHoverTooltip
                      :user-id="reply.upUserId"
                      :user-name="getUserNm(reply.upUserId) || reply.upUserNm"
                      :user-dept="reply.upUserDept || ''"
                    >
                      <span
                        class="reference-content__writer reference-content__writer--link"
                        @click.stop="scrollToParentComment(activityNo + '_' + reply.upReplyNo)"
                      >
                        {{ t('task.home.referencePost', { name: getUserNm(reply.upUserId) || reply.upUserNm }) }}
                      </span>
                    </ActivityUserHoverTooltip>
                    <UiButton size="small" variant="ghost" class="reference-toggle-btn" @click.stop="toggleReplyReferenceExpanded(reply.replyNo)">
                      <i :class="['icon', 'icon-sm', isReplyReferenceExpanded(reply.replyNo) ? 'icon-chevron-up' : 'icon-chevron-down']"></i>
                        {{ isReplyReferenceExpanded(reply.replyNo) ? t('task.home.fold') : t('task.home.expand') }}
                    </UiButton>
                  </div>
                  <TinymceViewer :content="reply.upContent || feedback.content" compact-body class="comment-content" />
                </div>
              </div>

              <!-- 현재 댓글 내용 -->
              <!-- 답글 수정 섹션 SS -->
              <div class="edit-reply-section edit-reply-section--highlighted" v-if="editingReplyIds.has(reply.replyNo)">
                <TinymceEditor
                  v-model="editingReplyContents[reply.replyNo]"
                  :minHeight="150"
                  :maxHeight="350"
                  :resizable="editorResizable"
                  :placeholder="t('common.activityCard.replyContentPlaceholder')"
                />
                <div class="flex justify-between items-center comment-compose-footer">
                  <button
                    type="button"
                    class="attachment-btn action-btn"
                    @click="openFeedbackAttachment('editReply', reply.replyNo, reply.atchFileId)"
                  >
                    <Icon name="heroicons:paper-clip" size="20" />
                    {{ getEditAttachmentCount('editReply', reply.replyNo) }}
                  </button>
                  <div class="flex gap-2">
                    <UiButton variant="secondary-line" @click="cancelEditReply(reply)">{{ t('common.action.cancel') }}</UiButton>
                    <UiButton variant="primary" @click="saveEditReply(reply)" :disabled="isSavingEditReply.has(reply.replyNo) || isEditorEmpty(editingReplyContents[reply.replyNo])">{{ t('common.action.save') }}</UiButton>
                  </div>
                </div>
              </div>
              <!-- 답글 수정 섹션 EE -->
              <div v-else class="activity-feed-body">
                <TinymceViewer :content="reply.content" class="comment-content" zero-body-margin />
              </div>
            </div>
          </div>

          <!-- 대댓글 추가 섹션 SS -->
          <div v-if="addNestedReply.has(reply.replyNo)" :ref="el => (nestedReplySectionRefs[reply.replyNo] = el)" class="reply-section--highlighted">
            <div class="comment-container comment-container--nested">
              <div class="comment-card comment-card--my-comment comment-card--highlighted">
                <!-- 추가 댓글 내용 -->
                <div class="comment-content">
                  <TinymceEditor
                    v-model="addNestedReplyContent[reply.replyNo]"
                    :minHeight="150"
                    :maxHeight="350"
                    :resizable="editorResizable"
                    :placeholder="t('common.activityCard.commentContentPlaceholder')"
                  />
                </div>
                <div class="flex justify-between items-center comment-compose-footer">
                  <button type="button" class="attachment-btn action-btn" @click="openFeedbackAttachment('addNestedReply', reply.replyNo)">
                    <Icon name="heroicons:paper-clip" size="20" />
                    {{ getAttachmentBucket('addNestedReply', reply.replyNo).length }}
                  </button>
                  <div class="flex gap-2">
                    <UiButton variant="secondary-line" @click="addNestedReply.delete(reply.replyNo)">{{ t('common.action.cancel') }}</UiButton>
                    <UiButton variant="primary" @click="saveAddNestedReply(reply)" :disabled="isSavingAddNestedReply.has(reply.replyNo) || isEditorEmpty(addNestedReplyContent[reply.replyNo])">{{ t('common.action.save') }}</UiButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- 대댓글 추가 섹션 EE -->
        </div>
        <!-- 피드백 댓글 섹션 EE -->
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, nextTick, watch } from 'vue';
import { useCollaborationStore } from '~/composables/useCollaborationStore';
import { useCollaborationApi } from '~/composables/useCollaborationApi';
import { showAlert, showConfirmDialog } from '~/composables/useConfirmDialog';
import { useCommon } from '~/composables/useCommon';
import { useAttachmentApi } from '~/composables/attachment/useAttachmentApi';
import { useAttachmentStore } from '~/composables/attachment/useAttachmentStore';
import UiFileAttachment from '~/components/attachment/UiFileAttachment.vue';
import ActivityUserHoverTooltip from '~/components/domain/Activity/ActivityUserHoverTooltip.vue';
import ActivityDateHoverTooltip from '~/components/domain/Activity/ActivityDateHoverTooltip.vue';
import ActivityFeedbackCommentBtn from '~/components/domain/Activity/ActivityFeedbackCommentBtn.vue';
import ActivityPhotoGallery from '~/components/domain/Activity/ActivityPhotoGallery.vue';
import ReadStatusIndicator from '~/pages/screens/task/comp/ReadStatusIndicator.vue';
import { useTaskFilterI18n, translateResultStatusLabelWith } from '~/composables/task/useTaskFilterI18n';

const { formatDate, isEmpty } = useCommon();
const { t } = useI18n();
const highlightWritingFeedback = computed(() => t('common.activityCard.writingFeedback'));
const highlightWritingReply = computed(() => t('common.activityCard.writingReply'));
const highlightEditingFeedback = computed(() => t('common.activityCard.editingFeedback'));
const highlightEditingReply = computed(() => t('common.activityCard.editingReplyLabel'));
const highlightEditingActivity = computed(() => t('common.activityCard.editingActivity'));
const { fetchAtchFileList, appendFileToFormData } = useAttachmentApi();
const { deleteFileIds } = useAttachmentStore();

/**
 * ActivityCard 컴포넌트 - 에디터 수정 기능 포함
 */

// 사용자 세션 정보 가져오기
const { userInfo } = useUserStore();
const {
  findYear,
  status,
  homeStatus,
  isBoard,
  selectedBoard,
  resultStatusCdOptions,
  statusCdOptions,
  isFileAttachmentModalOpen_detailActivity,
  attachmentFiles_detailActivity,
  isFileAttachmentModalOpen_editActivity,
  attachmentFiles_editActivity,
  isFileAttachmentModalOpen_feedback,
  attachmentFiles_feedback,
  feedbackAttachmentApplyHandler,
  feedbackAttachmentCancelHandler,
  isReadUserCntVisible,
  handleReadArticle,
  homeTaskModalClear,
  isTaskModalOpen,
  handleFetchActivityList,
  getTaskStatusColor,
  clearUnreadCountByCardId,
  handlePlanSearch,
  handleActivitySearch,
  isEditorEmpty
} = useCollaborationStore();

const { localizeResultStatusOptions } = useTaskFilterI18n();
const localizedResultStatusCdOptions = localizeResultStatusOptions(resultStatusCdOptions);

const { saveActivity, deleteActivity } = useCollaborationApi();
const { getUserNm } = useCommonStore();

// activityItems 및 각 feedbackList 내 activityUnreadCnt까지 0인지 체크하는 함수
function isAllActivityAndFeedbackUnreadCntZero(activityItems) {
  if (!Array.isArray(activityItems) || activityItems.length === 0) return false;
  return activityItems.every(item => {
    // 활동 자체가 0인지
    const isActivityZero = item.activityUnreadCnt === 0;
    // feedbackList가 배열이면, 모든 feedback의 activityUnreadCnt가 0인지
    const isAllFeedbackZero = Array.isArray(item.feedbackList) ? item.feedbackList.every(feedback => feedback.activityUnreadCnt === 0) : true;
    return isActivityZero && isAllFeedbackZero;
  });
}

// Activity 레벨 NEW 클릭 핸들러
const handleActivityNewClick = async () => {
  await handleReadArticle({
    readUnit: 'activity',
    boardId: status.boardId ? status.boardId : homeStatus.value.boardId,
    planId: props.planId,
    activityNo: props.activityNo,
    replyNo: '',
    upReplyNo: '',
  });

  // 보드 활동 목록 갱신
  const card = {
    cardId: props.planId,
  };
  const activityItems = await handleFetchActivityList(card.cardId);

  // 모든 activityUnreadCnt 및 feedbackList의 activityUnreadCnt가 0이면 unread를 리셋
  if (isAllActivityAndFeedbackUnreadCntZero(activityItems)) {
    clearUnreadCountByCardId(props.planId);
  }

  // 부모 컴포넌트에 활동 내역 업데이트 알림
  emit('activity-updated', activityItems);
};

// Feedback 레벨 NEW 클릭 핸들러
const handleFeedbackNewClick = async feedback => {
  await handleReadArticle({
    readUnit: 'feedback',
    boardId: status.boardId ? status.boardId : homeStatus.value.boardId,
    planId: props.planId,
    activityNo: props.activityNo,
    replyNo: feedback.replyNo,
    upReplyNo: '',
  });

  // 보드 활동 목록 갱신
  const card = {
    cardId: props.planId,
  };
  const activityItems = await handleFetchActivityList(card.cardId);

  // 모든 activityUnreadCnt 및 feedbackList의 activityUnreadCnt가 0이면 unread를 리셋
  if (isAllActivityAndFeedbackUnreadCntZero(activityItems)) {
    clearUnreadCountByCardId(props.planId);
  }

  // 부모 컴포넌트에 활동 내역 업데이트 알림
  emit('activity-updated', activityItems);
};

// Reply 레벨 NEW 클릭 핸들러
const handleReplyNewClick = async reply => {
  await handleReadArticle({
    readUnit: 'reply',
    boardId: status.boardId ? status.boardId : homeStatus.value.boardId,
    planId: props.planId,
    activityNo: props.activityNo,
    replyNo: reply.replyNo,
    upReplyNo: reply.upReplyNo || '',
  });

  // 보드 활동 목록 갱신
  const card = {
    cardId: props.planId,
  };
  const activityItems = await handleFetchActivityList(card.cardId);

  // 모든 activityUnreadCnt 및 feedbackList의 activityUnreadCnt가 0이면 unread를 리셋
  if (isAllActivityAndFeedbackUnreadCntZero(activityItems)) {
    clearUnreadCountByCardId(props.planId);
  }

  // 부모 컴포넌트에 활동 내역 업데이트 알림
  emit('activity-updated', activityItems);
};

// 수정 모드 상태
const isEditing = ref(false);
const editedContent = ref('');
const editStatus = ref('');
const editEvaluation = ref('');
const editCurrentDate = ref('');
const addComment = ref(false);
const addFeedbackContent = ref('');
const editingFeedbackIds = ref(new Set());
const editingFeedbackContents = ref({});
const addReply = ref(new Set());
const addReplyContent = ref({});
const editingReplyIds = ref(new Set());
const editingReplyContents = ref({});
const addNestedReply = ref(new Set());
const addNestedReplyContent = ref({});
const replySectionRefs = ref({});
const nestedReplySectionRefs = ref({});
const expandedReplyReferences = ref({});
const addCommentSectionRef = ref(null);

const toggleReplyReferenceExpanded = replyNo => {
  expandedReplyReferences.value = {
    ...expandedReplyReferences.value,
    [replyNo]: !expandedReplyReferences.value[replyNo],
  };
};

const isReplyReferenceExpanded = replyNo => !!expandedReplyReferences.value[replyNo];

// 피드백/답글 첨부파일
const attachmentFiles_addFeedback = ref([]);
const attachmentFiles_editFeedback = ref({});
const attachmentFiles_addReply = ref({});
const attachmentFiles_editReply = ref({});
const attachmentFiles_addNestedReply = ref({});
const commentAttachmentDeleteIds = ref({});

const attachmentStoreKey = (store, key = '') => `${store}:${key}`;

const getAttachmentBucket = (store, key = '') => {
  if (store === 'addFeedback') {
    return attachmentFiles_addFeedback.value;
  }
  const mapRef = {
    editFeedback: attachmentFiles_editFeedback,
    addReply: attachmentFiles_addReply,
    editReply: attachmentFiles_editReply,
    addNestedReply: attachmentFiles_addNestedReply,
  }[store];
  if (!mapRef) return [];
  if (!mapRef.value[key]) {
    mapRef.value[key] = [];
  }
  return mapRef.value[key];
};

const setAttachmentBucket = (store, key, files) => {
  if (store === 'addFeedback') {
    attachmentFiles_addFeedback.value = files;
    return;
  }
  const mapRef = {
    editFeedback: attachmentFiles_editFeedback,
    addReply: attachmentFiles_addReply,
    editReply: attachmentFiles_editReply,
    addNestedReply: attachmentFiles_addNestedReply,
  }[store];
  if (mapRef) {
    mapRef.value[key] = files;
  }
};

const getAttachmentDeleteIds = (store, key = '') => commentAttachmentDeleteIds.value[attachmentStoreKey(store, key)] || [];

const clearAttachmentBucket = (store, key = '') => {
  setAttachmentBucket(store, key, []);
  delete commentAttachmentDeleteIds.value[attachmentStoreKey(store, key)];
};

const preloadEditAttachmentBucket = async (store, key, atchFileId = '') => {
  delete commentAttachmentDeleteIds.value[attachmentStoreKey(store, key)];

  if (atchFileId) {
    const atchFileList = await fetchAtchFileList({ atchFileId });
    setAttachmentBucket(store, key, atchFileList.fileList || []);
  } else {
    setAttachmentBucket(store, key, []);
  }
};

/** 피드백/답글 수정 모드 첨부파일 개수 (삭제 체크된 기존 파일 제외) */
const getEditAttachmentCount = (store, key = '') => {
  const files = getAttachmentBucket(store, key) || [];
  const deletedIds = getAttachmentDeleteIds(store, key);

  return files.filter(file => {
    if (!file.atchFileId) return true;
    return !deletedIds.some(id => String(id) === String(file.fileSn));
  }).length;
};

const buildActivitySaveFormData = (params, { files = [], atchFileId = '', deleteIds = [] } = {}) => {
  const formData = new FormData();
  const newFiles = (files || []).filter(f => f instanceof File);

  if (newFiles.length > 0 || deleteIds.length > 0) {
    appendFileToFormData({
      formData,
      files: newFiles,
      deleteFileIds: deleteIds,
      fileMeta: {
        inputFileNames: ['upload' + Date.now()],
        atchFileIds: atchFileId ? [atchFileId] : ['NULL'],
      },
    });
  }

  formData.append('atchFileId', atchFileId || '');
  Object.keys(params).forEach(k => {
    formData.append(k, params[k]);
  });
  return formData;
};

/** 활동 입력 첨부와 동일 모달(app.vue 전역 CollaborationTaskModal) */
const openFeedbackAttachment = async (store, key = '', atchFileId = '') => {
  const savedDeleteIds = [...getAttachmentDeleteIds(store, key)];
  deleteFileIds.value = savedDeleteIds;

  let initial = [...getAttachmentBucket(store, key)];
  if (!initial.length && atchFileId) {
    const atchFileList = await fetchAtchFileList({ atchFileId });
    initial = atchFileList.fileList || [];
    setAttachmentBucket(store, key, initial);
  }

  attachmentFiles_feedback.value = initial;
  feedbackAttachmentApplyHandler.value = () => {
    setAttachmentBucket(store, key, [...attachmentFiles_feedback.value]);
    commentAttachmentDeleteIds.value[attachmentStoreKey(store, key)] = [...deleteFileIds.value];
  };
  feedbackAttachmentCancelHandler.value = () => {
    deleteFileIds.value = savedDeleteIds;
  };

  isFileAttachmentModalOpen_feedback.value = true;
};

const openCommentAttachmentView = async atchFileId => {
  isFileAttachmentModalOpen_detailActivity.value = true;
  attachmentFiles_detailActivity.value = [];
  const atchFileList = await fetchAtchFileList({ atchFileId });
  attachmentFiles_detailActivity.value = atchFileList.fileList || [];
};

// 저장 중 상태 관리
const isSavingAddComment = ref(false);
const isSavingEditComment = ref(new Set());
const isSavingAddReply = ref(new Set());
const isSavingEditReply = ref(new Set());
const isSavingAddNestedReply = ref(new Set());

// Props 정의
const props = defineProps({
  // 활동 고유 ID
  activityNo: {
    type: [String, Number],
    required: true,
  },
  planId: {
    type: [String, Number],
    required: true,
  },
  // 평가 상태 (우수, 보통, 미흡)
  evaluation: {
    type: String,
    default: '보통',
    validator: value => ['우수', '보통', '미흡'].includes(value),
  },
  // 현재 날짜
  currentDate: {
    type: String,
    default: () => new Date().toISOString().split('T')[0],
  },
  // 생성 날짜
  createdDate: {
    type: String,
    default: () => new Date().toISOString().split('T')[0],
  },
  //작성자
  createUserNm: {
    type: String,
  },
  //작성자
  createUserDept: {
    type: String,
  },
  //읽은사람들 수
  readUserCnt: {
    type: String,
  },
  //읽은사람들
  readUserStr: {
    type: String,
  },
  //안읽은 활동수
  activityUnreadCnt: {
    type: String,
  },
  // 활동 내용 (HTML)
  content: {
    type: String,
    default: '<ul><li>활동 내용이 없습니다.</li></ul>',
  },
  activityDt: {
    type: String,
    default: '',
  },
  statusColor: {
    type: String,
    default: '',
  },
  resultStatusNm: {
    type: String,
    default: '',
  },
  // 피드백 목록
  feedbackList: {
    type: Array,
    default: () => [],
  },
  createUserId: {
    type: String,
    default: '',
  },
  statusCd: {
    type: String,
    default: '',
  },
  resultStatusCd: {
    type: String,
    default: '',
  },
  replyCnt: {
    type: String,
    default: '',
  },
  atchFileId: {
    type: String,
    default: '',
  },
  atchFileCnt: {
    type: Number,
    default: 0,
  },
  imgPaths: {
    type: String,
    default: '',
  },
  thumbPaths: {
    type: String,
    default: '',
  },
  /** IBMS 연동 활동 ID. 값이 있으면 활동 본문 수정/삭제 버튼을 숨긴다 */
  ibmsActivityId: {
    type: [String, Number],
    default: '',
  },
  activityReadOnly: {
    type: Boolean,
    default: false,
  },
  feedbackReadOnly: {
    type: Boolean,
    default: false,
  },
  /** activityReadOnly일 때 수정/삭제 버튼 표시 (기본 false = 숨김) */
  showActivityEditDelete: {
    type: Boolean,
    default: false,
  },
  showReadStatus: {
    type: Boolean,
    default: true,
  },
  /** 활동/피드백 에디터를 드래그 리사이즈 모드로 표시 (업무/협업 화면 전용, 기본값 false) */
  editorResizable: {
    type: Boolean,
    default: false,
  },
});

/** IBMS_ACTIVITY_ID가 있으면 연동 활동이라 본문 수정/삭제를 막는다 */
const hasIbmsActivityId = computed(() => {
  const value = props.ibmsActivityId;
  if (value === null || value === undefined) return false;
  return String(value).trim() !== '';
});

watch(
  () => props.feedbackList,
  list => {
    list.forEach(feedback => {
      console.log('[ActivityCard][1차 피드백]', props.activityNo, feedback.replyNo, feedback);
      feedback.feedbackReplyList?.forEach(reply => {
        console.log('[ActivityCard][reply]', props.activityNo, reply.replyNo, reply);
      });
    });
  },
  { immediate: true, deep: true },
);

// 첨부파일 조회
const openFileAttachmentModal_detailActivity = async () => {
  isFileAttachmentModalOpen_detailActivity.value = true;
  attachmentFiles_detailActivity.value = [];

  const atchFileList = await fetchAtchFileList({
    atchFileId: props.atchFileId,
  });
  attachmentFiles_detailActivity.value = atchFileList.fileList;
};

// 수정모드 첨부파일 수정
const openFileAttachmentModal_editActivity = async () => {
  isFileAttachmentModalOpen_editActivity.value = true;

  if (attachmentFiles_editActivity.value.length > 0 || !props.atchFileId) return;

  const atchFileList = await fetchAtchFileList({
    atchFileId: props.atchFileId,
  });
  attachmentFiles_editActivity.value = atchFileList.fileList || [];
};

/** 수정 모드 첨부파일 개수 (삭제 체크된 기존 파일 제외) */
const editActivityAttachmentCount = computed(() => {
  const files = attachmentFiles_editActivity.value || [];
  const deletedIds = deleteFileIds.value || [];

  return files.filter(file => {
    if (!file.atchFileId) return true;
    return !deletedIds.some(id => String(id) === String(file.fileSn));
  }).length;
});

const statusNm = computed(() =>
  translateResultStatusLabelWith(props.resultStatusCd, t, t('task.common.resultStatus.001')),
);

// 이벤트 정의
const emit = defineEmits([
  'edit',
  'delete',
  'reply',
  'edit-comment',
  'delete-comment',
  'reply-comment',
  'edit-reply',
  'delete-reply',
  'add-comment',
  'activity-updated',
]);

const showAddCommentModal = async () => {
  handleReplySectionInit();
  addFeedbackContent.value = '';
  attachmentFiles_addFeedback.value = [];
  delete commentAttachmentDeleteIds.value[attachmentStoreKey('addFeedback')];
  const wasOpen = addComment.value;
  addComment.value = !addComment.value;

  // 답글 작성 UI가 열릴 때만 스크롤 이동
  if (!wasOpen && addComment.value) {
    // DOM 업데이트 후 스크롤 이동
    await nextTick();
    if (addCommentSectionRef.value) {
      addCommentSectionRef.value.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }
};

const cancelAddComment = () => {
  addComment.value = false;
  attachmentFiles_addFeedback.value = [];
  delete commentAttachmentDeleteIds.value[attachmentStoreKey('addFeedback')];
};

/** 홈 탭 배경(사이드바·활동 피드) 동기화 — 팝업이 열려 있으면 닫지 않음 */
const syncHomeAfterFeedbackChange = async () => {
  if (!isTaskModalOpen.value) {
    await homeTaskModalClear();
  }
  await handlePlanSearch('N', false);
  await handleActivitySearch(true);
};

// 피드백 저장
const saveAddComment = async () => {
  // 이미 저장 중이면 중복 실행 방지
  if (isSavingAddComment.value) {
    return;
  }

  isSavingAddComment.value = true;

  try {
    const params = {
      findYear: findYear.value,
      boardId: status.boardId ? status.boardId : homeStatus.value.boardId,
      planId: props.planId ? props.planId : homeStatus.value.planId,
      activityNo: props.activityNo,
      upActivityNo: props.activityNo,
      replyNo: '',
      content: isEmpty(addFeedbackContent.value) ? '<p><br></p>' : addFeedbackContent.value,
    };

    const formData = buildActivitySaveFormData(params, {
      files: attachmentFiles_addFeedback.value,
      atchFileId: '',
      deleteIds: getAttachmentDeleteIds('addFeedback'),
    });

    await saveActivity(formData);
    showAlert('', t('common.activityCard.alert.feedbackSaved'));

    if (isBoard.value) {
      const card = {
        cardId: props.planId,
      };

      const activityItems = await handleFetchActivityList(card.cardId);
      // 부모 컴포넌트에 활동 내역 업데이트 알림
      emit('activity-updated', activityItems);
      addComment.value = false;
      attachmentFiles_addFeedback.value = [];
    } else {
      // 활동 목록 재조회 + 폼 닫기
      const card = { cardId: props.planId };
      const activityItems = await handleFetchActivityList(card.cardId);
      emit('activity-updated', activityItems);
      addComment.value = false;
      addFeedbackContent.value = '';
      attachmentFiles_addFeedback.value = [];
      await syncHomeAfterFeedbackChange();
    }
  } catch (error) {
    console.error('피드백 저장 중 오류 발생:', error);
    showAlert('', t('common.activityCard.alert.feedbackSaveError'));
  } finally {
    isSavingAddComment.value = false;
  }
};

// 피드백 답글 저장
const saveAddReply = async feedback => {
  // 이미 저장 중이면 중복 실행 방지
  if (isSavingAddReply.value.has(feedback.replyNo)) {
    return;
  }

  isSavingAddReply.value.add(feedback.replyNo);

  try {
    const params = {
      findYear: findYear.value,
      boardId: status.boardId ? status.boardId : homeStatus.value.boardId,
      planId: props.planId ? props.planId : homeStatus.value.planId,
      activityNo: props.activityNo,
      upActivityNo: props.activityNo,
      upReplyNo: feedback.replyNo,
      isNewNestedReply: 'Y', // 수정 시에는 값을 담지 않음.
      replyNo: '',
      content: isEmpty(addReplyContent.value[feedback.replyNo]) ? '<p><br></p>' : addReplyContent.value[feedback.replyNo],
    };

    const formData = buildActivitySaveFormData(params, {
      files: getAttachmentBucket('addReply', feedback.replyNo),
      atchFileId: '',
      deleteIds: getAttachmentDeleteIds('addReply', feedback.replyNo),
    });

    await saveActivity(formData);
    showAlert('', t('common.activityCard.alert.replySaved'));

    if (isBoard.value) {
      const card = {
        cardId: props.planId,
      };

      const activityItems = await handleFetchActivityList(card.cardId);
      // 부모 컴포넌트에 활동 내역 업데이트 알림
      emit('activity-updated', activityItems);
      addReply.value.delete(feedback.replyNo);
      delete addReplyContent.value[feedback.replyNo];
      clearAttachmentBucket('addReply', feedback.replyNo);
    } else {
      // 활동 목록 재조회 + 폼 닫기
      const card = { cardId: props.planId };
      const activityItems = await handleFetchActivityList(card.cardId);
      emit('activity-updated', activityItems);
      addReply.value.delete(feedback.replyNo);
      delete addReplyContent.value[feedback.replyNo];
      clearAttachmentBucket('addReply', feedback.replyNo);
      await syncHomeAfterFeedbackChange();
    }
  } catch (error) {
    console.error('답글 저장 중 오류 발생:', error);
    showAlert('', t('common.activityCard.alert.replySaveError'));
  } finally {
    isSavingAddReply.value.delete(feedback.replyNo);
  }
};

// 대댓글 저장
const saveAddNestedReply = async reply => {
  // 이미 저장 중이면 중복 실행 방지
  if (isSavingAddNestedReply.value.has(reply.replyNo)) {
    return;
  }

  isSavingAddNestedReply.value.add(reply.replyNo);

  try {
    const params = {
      findYear: findYear.value,
      boardId: status.boardId ? status.boardId : homeStatus.value.boardId,
      planId: props.planId ? props.planId : homeStatus.value.planId,
      activityNo: props.activityNo,
      upActivityNo: props.activityNo,
      upReplyNo: reply.replyNo,
      isNewNestedReply: 'Y', // 수정 시에는 값을 담지 않음.
      replyNo: '',
      content: isEmpty(addNestedReplyContent.value[reply.replyNo]) ? '<p><br></p>' : addNestedReplyContent.value[reply.replyNo],
    };

    const formData = buildActivitySaveFormData(params, {
      files: getAttachmentBucket('addNestedReply', reply.replyNo),
      atchFileId: '',
      deleteIds: getAttachmentDeleteIds('addNestedReply', reply.replyNo),
    });

    await saveActivity(formData);
    showAlert('', t('common.activityCard.alert.replySaved'));

    if (isBoard.value) {
      const card = {
        cardId: props.planId,
      };

      const activityItems = await handleFetchActivityList(card.cardId);
      // 부모 컴포넌트에 활동 내역 업데이트 알림
      emit('activity-updated', activityItems);
      addNestedReply.value.delete(reply.replyNo);
      delete addNestedReplyContent.value[reply.replyNo];
      clearAttachmentBucket('addNestedReply', reply.replyNo);
    } else {
      // 활동 목록 재조회 + 폼 닫기
      const card = { cardId: props.planId };
      const activityItems = await handleFetchActivityList(card.cardId);
      emit('activity-updated', activityItems);
      addNestedReply.value.delete(reply.replyNo);
      delete addNestedReplyContent.value[reply.replyNo];
      clearAttachmentBucket('addNestedReply', reply.replyNo);
      await syncHomeAfterFeedbackChange();
    }
  } catch (error) {
    console.error('대댓글 저장 중 오류 발생:', error);
    showAlert('', t('common.activityCard.alert.nestedReplySaveError'));
  } finally {
    isSavingAddNestedReply.value.delete(reply.replyNo);
  }
};

const handleStartEditComment = async feedback => {
  handleReplySectionInit();
  await preloadEditAttachmentBucket('editFeedback', feedback.replyNo, feedback.atchFileId);
  editingFeedbackIds.value = new Set();
  editingFeedbackIds.value.add(feedback.replyNo);
  editingFeedbackContents.value[feedback.replyNo] = feedback.content;
  addReply.value.delete(feedback.replyNo);
};

const handleStartEditReply = async reply => {
  handleReplySectionInit();
  await preloadEditAttachmentBucket('editReply', reply.replyNo, reply.atchFileId);
  editingReplyIds.value = new Set();
  editingReplyIds.value.add(reply.replyNo);
  editingReplyContents.value[reply.replyNo] = reply.content;
  addReply.value.clear();
};

const cancelEditComment = feedback => {
  editingFeedbackIds.value.delete(feedback.replyNo);
  delete editingFeedbackContents.value[feedback.replyNo];
  clearAttachmentBucket('editFeedback', feedback.replyNo);
};

const cancelEditReply = reply => {
  editingReplyIds.value.delete(reply.replyNo);
  delete editingReplyContents.value[reply.replyNo];
  clearAttachmentBucket('editReply', reply.replyNo);
};

// 피드백 답글버튼
const handleStartReply = async feedback => {
  handleReplySectionInit();
  if (addReply.value.has(feedback.replyNo)) {
    addReply.value.delete(feedback.replyNo);
    delete addReplyContent.value[feedback.replyNo];
    delete replySectionRefs.value[feedback.replyNo];
  } else {
    addReply.value.add(feedback.replyNo);
    addReplyContent.value[feedback.replyNo] = '';
    // DOM 업데이트 후 스크롤 이동
    await nextTick();
    const replySection = replySectionRefs.value[feedback.replyNo];
    if (replySection) {
      replySection.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }
};

const handleStartNestedReply = async reply => {
  handleReplySectionInit();
  if (addNestedReply.value.has(reply.replyNo)) {
    addNestedReply.value.delete(reply.replyNo);
    delete addNestedReplyContent.value[reply.replyNo];
    delete nestedReplySectionRefs.value[reply.replyNo];
  } else {
    addNestedReply.value.add(reply.replyNo);
    addNestedReplyContent.value[reply.replyNo] = '';
    // DOM 업데이트 후 스크롤 이동
    await nextTick();
    const nestedReplySection = nestedReplySectionRefs.value[reply.replyNo];
    if (nestedReplySection) {
      nestedReplySection.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }
};

// 피드백 수정
const saveEditComment = async feedback => {
  // 이미 저장 중이면 중복 실행 방지
  if (isSavingEditComment.value.has(feedback.replyNo)) {
    return;
  }

  isSavingEditComment.value.add(feedback.replyNo);

  try {
    const params = {
      findYear: findYear.value,
      boardId: status.boardId ? status.boardId : homeStatus.value.boardId,
      planId: props.planId ? props.planId : homeStatus.value.planId,
      activityNo: props.activityNo,
      upActivityNo: feedback.activityNo,
      replyNo: feedback.replyNo,
      content: isEmpty(editingFeedbackContents.value[feedback.replyNo]) ? '<p><br></p>' : editingFeedbackContents.value[feedback.replyNo],
    };

    const formData = buildActivitySaveFormData(params, {
      files: getAttachmentBucket('editFeedback', feedback.replyNo),
      atchFileId: feedback.atchFileId || '',
      deleteIds: getAttachmentDeleteIds('editFeedback', feedback.replyNo),
    });

    await saveActivity(formData);

    showAlert('', t('common.activityCard.alert.feedbackUpdated'));

    if (isBoard.value) {
      // 보드 활동
      const card = {
        cardId: props.planId,
      };
      const activityItems = await handleFetchActivityList(card.cardId);
      // 부모 컴포넌트에 활동 내역 업데이트 알림
      emit('activity-updated', activityItems);

      editingFeedbackIds.value.delete(feedback.replyNo);
      delete editingFeedbackContents.value[feedback.replyNo];
      clearAttachmentBucket('editFeedback', feedback.replyNo);
    } else {
      // 활동 목록 재조회 + 편집 모드 해제
      const card = { cardId: props.planId };
      const activityItems = await handleFetchActivityList(card.cardId);
      emit('activity-updated', activityItems);
      editingFeedbackIds.value.delete(feedback.replyNo);
      delete editingFeedbackContents.value[feedback.replyNo];
      clearAttachmentBucket('editFeedback', feedback.replyNo);
      await syncHomeAfterFeedbackChange();
    }
  } catch (error) {
    console.error('피드백 수정 중 오류 발생:', error);
    showAlert('', t('common.activityCard.alert.feedbackUpdateError'));
  } finally {
    isSavingEditComment.value.delete(feedback.replyNo);
  }
};

// 피드백 답글 수정
const saveEditReply = async reply => {
  // 이미 저장 중이면 중복 실행 방지
  if (isSavingEditReply.value.has(reply.replyNo)) {
    return;
  }

  isSavingEditReply.value.add(reply.replyNo);

  try {
    const params = {
      findYear: findYear.value,
      boardId: status.boardId ? status.boardId : homeStatus.value.boardId,
      planId: props.planId ? props.planId : homeStatus.value.planId,
      activityNo: props.activityNo,
      upActivityNo: reply.activityNo,
      replyNo: reply.replyNo,
      upReplyNo: reply.upReplyNo,
      content: isEmpty(editingReplyContents.value[reply.replyNo]) ? '<p><br></p>' : editingReplyContents.value[reply.replyNo],
    };

    const formData = buildActivitySaveFormData(params, {
      files: getAttachmentBucket('editReply', reply.replyNo),
      atchFileId: reply.atchFileId || '',
      deleteIds: getAttachmentDeleteIds('editReply', reply.replyNo),
    });

    await saveActivity(formData);

    showAlert('', t('common.activityCard.alert.replyUpdated'));

    if (isBoard.value) {
      // 보드 활동
      const card = {
        cardId: props.planId,
      };
      const activityItems = await handleFetchActivityList(card.cardId);
      // 부모 컴포넌트에 활동 내역 업데이트 알림
      emit('activity-updated', activityItems);

      editingReplyIds.value.delete(reply.replyNo);
      delete editingReplyContents.value[reply.replyNo];
      clearAttachmentBucket('editReply', reply.replyNo);
    } else {
      // 활동 목록 재조회 + 편집 모드 해제
      const card = { cardId: props.planId };
      const activityItems = await handleFetchActivityList(card.cardId);
      emit('activity-updated', activityItems);
      editingReplyIds.value.delete(reply.replyNo);
      delete editingReplyContents.value[reply.replyNo];
      clearAttachmentBucket('editReply', reply.replyNo);
      await syncHomeAfterFeedbackChange();
    }
  } catch (error) {
    console.error('답글 수정 중 오류 발생:', error);
    showAlert('', t('common.activityCard.alert.replyUpdateError'));
  } finally {
    isSavingEditReply.value.delete(reply.replyNo);
  }
};

const handleDeleteComment = async feedback => {
  showConfirmDialog(t('common.activityCard.alert.feedbackDeleteTitle'), t('common.activityCard.alert.feedbackDeleteConfirm'), async () => {
    const params = {
      findYear: findYear.value,
      boardId: status.boardId ? status.boardId : homeStatus.value.boardId,
      planId: props.planId ? props.planId : homeStatus.value.planId,
      activityNo: props.activityNo,
      upActivityNo: props.activityNo,
      replyNo: feedback.replyNo,
    };

    await deleteActivity(params);

    showAlert('', t('common.activityCard.alert.feedbackDeleted'));

    if (isBoard.value) {
      const card = {
        cardId: props.planId,
      };
      // 활동 내역 조회
      const activityItems = await handleFetchActivityList(card.cardId);
      // 부모 컴포넌트에 활동 내역 업데이트 알림
      emit('activity-updated', activityItems);
    } else {
      // 홈일 때도 activity-updated 이벤트 emit (CollaborationFeed 리로드를 위해)
      const card = {
        cardId: props.planId,
      };
      const activityItems = await handleFetchActivityList(card.cardId);
      emit('activity-updated', activityItems);
      await syncHomeAfterFeedbackChange();
    }
  });
};

const handleDeleteReply = async reply => {
  showConfirmDialog(t('common.activityCard.alert.replyDeleteTitle'), t('common.activityCard.alert.replyDeleteConfirm'), async () => {
    const params = {
      findYear: findYear.value,
      boardId: status.boardId ? status.boardId : homeStatus.value.boardId,
      planId: props.planId ? props.planId : homeStatus.value.planId,
      activityNo: props.activityNo,
      upActivityNo: props.activityNo,
      replyNo: reply.replyNo,
    };

    await deleteActivity(params);

    showAlert('', t('common.activityCard.alert.replyDeleted'));

    if (isBoard.value) {
      const card = {
        cardId: props.planId,
      };
      // 활동 내역 조회
      const activityItems = await handleFetchActivityList(card.cardId);
      // 부모 컴포넌트에 활동 내역 업데이트 알림
      emit('activity-updated', activityItems);
    } else {
      // 홈일 때도 activity-updated 이벤트 emit (CollaborationFeed 리로드를 위해)
      const card = {
        cardId: props.planId,
      };
      const activityItems = await handleFetchActivityList(card.cardId);
      emit('activity-updated', activityItems);
      await syncHomeAfterFeedbackChange();
    }
  });
};

/**
 * 수정 모드 시작
 */
const startEdit = async () => {
  handleReplySectionInit();
  deleteFileIds.value = [];

  if (props.atchFileId) {
    const atchFileList = await fetchAtchFileList({
      atchFileId: props.atchFileId,
    });
    attachmentFiles_editActivity.value = atchFileList.fileList || [];
  } else {
    attachmentFiles_editActivity.value = [];
  }

  isEditing.value = true;
  editedContent.value = props.content || ''; // cleanText 제거하여 원본 데이터 유지
  editStatus.value = props.resultStatusCd;
  editEvaluation.value = props.statusCd;
  editCurrentDate.value = props.currentDate;
};

// 활동 저장
const saveEdit = async () => {
  isEditing.value = false;

  const params = {
    findYear: findYear.value,
    boardId: status.boardId ? status.boardId : homeStatus.value.boardId,
    planId: props.planId ? props.planId : homeStatus.value.planId,
    activityNo: props.activityNo,
    upActivityNo: '',
    replyNo: '',
    activityDt: formatDate(editCurrentDate.value),
    content: isEmpty(editedContent.value) ? '<p><br></p>' : editedContent.value,
    statusCd: editEvaluation.value,
    resultStatusCd: editStatus.value,
  };

  const formData = buildActivitySaveFormData(params, {
    files: attachmentFiles_editActivity.value,
    atchFileId: props.atchFileId || '',
    deleteIds: deleteFileIds.value,
  });

  // 활동 내역 저장
  await saveActivity(formData);
  showAlert('', t('common.activityCard.alert.activityUpdated'));

  if (isBoard.value) {
    const card = {
      cardId: props.planId,
    };
    // 활동 내역 조회
    const activityItems = await handleFetchActivityList(card.cardId);
    attachmentFiles_editActivity.value = [];
    deleteFileIds.value = [];
    // 부모 컴포넌트에 활동 내역 업데이트 알림
    emit('activity-updated', activityItems);
    selectedBoard.value = null;
    await nextTick();
    selectedBoard.value = status.boardId;
  } else {
    // 활동 목록 재조회
    const card = { cardId: props.planId };
    const activityItems = await handleFetchActivityList(card.cardId);
    attachmentFiles_editActivity.value = [];
    deleteFileIds.value = [];
    emit('activity-updated', activityItems);
    await syncHomeAfterFeedbackChange();
  }
};

/**
 * 수정 취소
 */
const cancelEdit = () => {
  isEditing.value = false;
  editedContent.value = '';
  attachmentFiles_editActivity.value = [];
  deleteFileIds.value = [];
};

/**
 * 삭제 버튼 클릭 처리
 */
const handleActivityDelete = async () => {
  showConfirmDialog(t('common.activityCard.alert.activityDeleteTitle'), t('common.activityCard.alert.activityDeleteConfirm'), async () => {
    const params = {
      findYear: findYear.value,
      boardId: status.boardId ? status.boardId : homeStatus.value.boardId,
      planId: props.planId ? props.planId : homeStatus.value.planId,
      activityNo: props.activityNo,
      upActivityNo: '',
      replyNo: '',
    };

    await deleteActivity(params);
    showAlert('', t('common.activityCard.alert.activityDeleted'));
    if (isBoard.value) {
      const card = {
        cardId: props.planId,
      };
      // 활동 내역 조회
      const activityItems = await handleFetchActivityList(card.cardId);
      // 부모 컴포넌트에 활동 내역 업데이트 알림
      emit('activity-updated', activityItems);
      selectedBoard.value = null;
      await nextTick();
      selectedBoard.value = status.boardId;
    } else {
      // 홈일 때도 activity-updated 이벤트 emit (CollaborationFeed 리로드를 위해)
      const card = {
        cardId: props.planId,
      };
      const activityItems = await handleFetchActivityList(card.cardId);
      emit('activity-updated', activityItems);
      await syncHomeAfterFeedbackChange();
    }
  });
};

// 답글 추가,수정 영역 초기화
const handleReplySectionInit = () => {
  isEditing.value = false;
  editedContent.value = '';
  editStatus.value = '';
  editEvaluation.value = '';
  editCurrentDate.value = '';

  addComment.value = false;
  addFeedbackContent.value = '';
  attachmentFiles_addFeedback.value = [];
  attachmentFiles_editFeedback.value = {};
  attachmentFiles_addReply.value = {};
  attachmentFiles_editReply.value = {};
  attachmentFiles_addNestedReply.value = {};
  commentAttachmentDeleteIds.value = {};
  editingFeedbackIds.value = new Set();
  editingFeedbackContents.value = {};

  addNestedReply.value.clear();
  addNestedReplyContent.value = {};
  nestedReplySectionRefs.value = {};

  addReply.value.clear();
  addReplyContent.value = {};
  replySectionRefs.value = {};
  editingReplyIds.value = new Set();
  editingReplyContents.value = {};
};

// 상위 댓글로 스크롤 이동 (모달·활동 목록 내부만 — ActivitySection과 동일 범위)
const activityCardContainerRef = ref(null);

const scrollToParentComment = async upActivityId => {
  await nextTick();
  const root = activityCardContainerRef.value?.closest('.activity-section');
  if (!root) return;

  const targetElement = root.querySelector(`[data-activity-id="${upActivityId}"]`);
  if (targetElement) {
    const commentCard = targetElement.querySelector('.comment-card');
    const scrollTarget = commentCard || targetElement;

    scrollTarget.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }
};
</script>

<style lang="scss" scoped>
@use '@/assets/scss/components/activity-tinymce-viewer' as activityTinymce;
.activity-card-container {
  margin-bottom: 0;
}

.ibms-activity-notice {
  margin: 0;
  color: #6d7882;
  font-size: 13px;
  font-weight: 400;
  line-height: 150%;
  white-space: nowrap;
}

/* 활동 카드 스타일 */
.activity-card {
  background: #f4f5f6; // 다른 사람 글 배경색 (연한 회색)
  border-radius: 4px;
  margin-bottom: 0;

  .activity-header {
    padding-bottom: 8px;
  }

  .activity-info {
    width: 100%;
    margin-bottom: 8px;

    .activity-date {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 4px;

      .activity-type {
        display: flex;
        align-items: center;
        flex: none;
        gap: 4px;
        font-family: 'Pretendard', sans-serif;
        font-weight: 700;
        font-size: 14px;
        line-height: 1.4;
        color: #0084ff;

        &::before {
          content: '';
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #0084ff;
        }

        &--excellent::before {
          background-color: #0084ff;
        }

        &--average::before {
          background-color: #f59e0b;
        }

        &--poor::before {
          background-color: #ef4444;
        }
      }

      .current-date {
        flex: none;
        font-family: 'Pretendard', sans-serif;
        font-weight: 400;
        font-size: 14px;
        line-height: 1.5;
        color: #6d7882;
        text-align: right;
      }

      .read-users {
        flex: none;
        font-family: 'Pretendard', sans-serif;
        font-weight: 400;
        font-size: 14px;
        line-height: 1.5;
        color: #6d7882;
        text-align: right;
      }
    }
  }

  .activity-user-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  .user-dot {
    color: #9ca3af;
    font-size: 12px;
  }

  .activity-user {
    display: flex;
    align-items: center;
    gap: 2px; // 확인된 간격

    .user-avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      overflow: hidden;
      background: #898989;
      border: 1px solid #cdd1d5;
      margin-right: 6px;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .user-name {
      font-family: 'Pretendard', sans-serif;
      font-weight: 700;
      font-size: 14px;
      line-height: 1.4;
      color: #00aaff; // 확인된 사용자명 색상
    }

    .user-divider {
      width: 2px;
      height: 16px;
      background-color: #b1b8be;
      margin: 0 8px;
    }

    .user-team {
      font-family: 'Pretendard', sans-serif;
      font-weight: 400;
      font-size: 14px;
      line-height: 1.5;
      color: #6d7882;
    }
  }

  @include activityTinymce.activity-feed-card-inner;

  .activity-footer {
    .activity-timestamp {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .timestamp-text {
        font-family: 'Pretendard', sans-serif;
        font-weight: 400;
        font-size: 14px;
        line-height: 1.5;
        color: #6d7882;
      }
    }
  }
}

/* 댓글 섹션 */
.comment-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-left: 24px;

  /* 피드백 추가 섹션 하이라이트 */
  &--highlighted {
    position: relative;
    margin: 8px 0;

    &::after {
      content: v-bind(highlightWritingFeedback);
      position: absolute;
      top: -8px;
      left: 12px;
      background: #0084ff;
      color: white;
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 500;
      z-index: 2;
      box-shadow: 0 2px 4px rgba(0, 132, 255, 0.3);
    }
  }
}

/* 답글 섹션 하이라이트 */
.reply-section--highlighted {
  position: relative;
  margin: 8px 0;

  &::after {
    content: v-bind(highlightWritingReply);
    position: absolute;
    top: -8px;
    left: 12px;
    background: #0084ff;
    color: white;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    z-index: 2;
    box-shadow: 0 2px 4px rgba(0, 132, 255, 0.3);
  }
}

/* 피드백 수정 섹션 하이라이트 */
.edit-comment-section--highlighted {
  position: relative;
  background: #e5f6ff;
  border: 2px solid #0084ff;
  border-radius: 8px;
  padding: 16px;
  margin: 8px 0;
  box-shadow: 0 4px 12px rgba(0, 132, 255, 0.2);
  transform: translateY(-2px);
  transition: all 0.3s ease;

  &::after {
    content: v-bind(highlightEditingFeedback);
    position: absolute;
    top: -8px;
    left: 12px;
    background: #0084ff;
    color: white;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    z-index: 2;
    box-shadow: 0 2px 4px rgba(0, 132, 255, 0.3);
  }

  &:hover {
    box-shadow: 0 6px 16px rgba(0, 132, 255, 0.25);
    transform: translateY(-3px);
  }
}

/* 답글 수정 섹션 하이라이트 */
.edit-reply-section--highlighted {
  position: relative;
  background: #e5f6ff;
  border: 2px solid #0084ff;
  border-radius: 8px;
  padding: 16px;
  margin: 8px 0;
  box-shadow: 0 4px 12px rgba(0, 132, 255, 0.2);
  transform: translateY(-2px);
  transition: all 0.3s ease;

  &::after {
    content: v-bind(highlightEditingReply);
    position: absolute;
    top: -8px;
    left: 12px;
    background: #0084ff;
    color: white;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    z-index: 2;
    box-shadow: 0 2px 4px rgba(0, 132, 255, 0.3);
  }

  &:hover {
    box-shadow: 0 6px 16px rgba(0, 132, 255, 0.25);
    transform: translateY(-3px);
  }
}

.comment-card {
  background: #f7faff;
  border-radius: 4px;
  margin-left: 8px;
  margin-top: 7px;

  @include activityTinymce.activity-feed-card-inner;

  &--header {
    padding-bottom: 8px;
  }

  &--simple {
    background: #ffffff;
    border: 1px solid #cdd1d5;
  }

  // 하이라이트된 댓글 카드 스타일
  &--highlighted {
    background: #e5f6ff;
    border: 2px solid #0084ff;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 132, 255, 0.2);
    transform: translateY(-2px);
    transition: all 0.3s ease;
    margin-top: 0;

    &:hover {
      box-shadow: 0 6px 16px rgba(0, 132, 255, 0.25);
      transform: translateY(-3px);
    }
  }
}

.comment-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #cdd1d5;
}

.comment-content {
  font-family: 'Pretendard', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 1.5;
  color: #464c53;
  margin-bottom: 8px;
}

.comment-footer {
  /* border-top: 1px solid #cdd1d5; */
}

.comment-user {
  width: 100%;
  display: flex;
  gap: 8px;
  justify-content: end;
  /*flex-direction: row-reverse;*/
}

.user-details {
  display: flex;
  gap: 4px;
  align-items: center;

  .comment-header & {
    flex-direction: row;
    align-items: center;
    gap: 8px;
  }
}

.user-name {
  font-family: 'Pretendard', sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 1.4;
  color: #464c53;
}

.user-team {
  font-family: 'Pretendard', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.5;
  color: #6d7882;
}

.comment-date {
  font-family: 'Pretendard', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.5;
  color: #6d7882;
}

/* 댓글 참조 부분 - 공통 스타일은 assets/scss/components/_comment-reference.scss */

.reference-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px; // 변경: margin-bottom에서 margin-top으로
  padding-top: 8px; // 변경: padding-bottom에서 padding-top으로
  border-top: 1px solid #cdd1d5; // 변경: border-bottom에서 border-top으로
  flex-direction: row-reverse;
  flex-wrap: wrap; // 이름 길어질 시 줄바꿈
}

.reference-user {
  display: flex;
  align-items: center;
  gap: 8px;

  .user-name {
    color: #1e2124; // 참조된 사용자명은 더 진한 색
  }
}

.reference-date {
  font-family: 'Pretendard', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.5;
  color: #6d7882;
}

/* 버튼 스타일 */
.icon-btn {
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #464c53; // 단순 댓글의 아이콘은 더 진한 색

  &:hover {
    background: rgba(70, 76, 83, 0.1);
  }
}

.comment-icon {
  width: 14px;
  height: 14px;
  color: inherit;
}

/* 유틸리티 클래스 */
.flex {
  display: flex;
}

.flex-col {
  flex-direction: column;
}

.gap-5 {
  gap: 5px;
}

.gap-1 {
  gap: 4px;
}

.items-center {
  align-items: center;
}

/* 에디터 컨테이너 스타일 */
.editor-container {
  .editor-top-controls {
    border-bottom: 1px solid #cdd1d5;
    padding-bottom: 16px;

    .control-label {
      font-family: 'Pretendard', sans-serif;
      font-weight: 500;
      font-size: 14px;
      color: #464c53;
      margin-right: 8px;
      white-space: nowrap;
    }

    .date-picker-wrapper {
      display: flex;
      align-items: center;
      gap: 8px;

      .date-picker {
        background: #ffffff;
        border-radius: 4px;

        :deep(.ui-datepicker__range-input) {
          background: #ffffff;
          border: 1px solid #cdd1d5;
          border-radius: 4px;
          padding: 8px 12px;
          min-width: 200px;

          .ui-datepicker__icon {
            color: #6d7882;
          }

          .ui-datepicker__placeholder {
            color: #6d7882;
            font-size: 14px;
          }

          .ui-datepicker__range-separator {
            color: #6d7882;
            margin: 0 8px;
          }
        }
      }
    }

    .select-group {
      display: flex;
      gap: 16px;

      .select-wrapper {
        display: flex;
        align-items: center;
        gap: 8px;
      }
    }
  }

  .editor-bottom-actions {
    border-top: 1px solid #cdd1d5;
    padding-top: 16px;
  }
}

.user-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #cdd1d5;
}

/* 기본 문단: 공백 유지, 여백 제거
   - white-space: pre-wrap → 작성 시 공백과 줄바꿈(\n)을 유지
   - margin/padding 제거로 불필요한 문단 간 간격 제거
   - line-height로 문단 간 시각적 간격만 일정하게 유지 */
:deep(.quill-content > p) {
  white-space: pre-wrap;
  margin: 0 !important;
  padding: 0 !important;
  line-height: 1.6;
}

/* 빈 문단(엔터 두 번)은 실제 한 줄 간격만 띄우도록 처리
   - <p></p> 또는 <p><br></p> 같은 빈 문단에 한 줄 높이만 적용 */
:deep(.quill-content > p:empty),
:deep(.quill-content > p > br:only-child) {
  display: block;
  height: 1.6em;
}

/* UL(불릿 목록) 설정
   - list-style-type: disc → 기본 불릿 모양 유지
   - list-style-position: outside → 불릿이 텍스트 밖에 위치하도록
   - padding-left: 2em → 들여쓰기 간격 확보
   - white-space: normal → li 안에서 pre-wrap 영향 제거 */
:deep(.quill-content ul) {
  list-style-type: disc !important;
  /* 불릿 */
  list-style-position: outside !important;
  padding-left: 2em !important;
  margin: 0.5em 0 !important;
  white-space: normal !important;
}

/* OL(번호 목록) 설정
   - list-style-type: decimal → 숫자 목록 표시 (1., 2., 3.)
   - UL과 동일한 들여쓰기 및 여백 규칙 적용 */
:deep(.quill-content ol) {
  list-style-type: decimal !important;
  /* 숫자 */
  list-style-position: outside !important;
  padding-left: 2em !important;
  margin: 0.5em 0 !important;
  white-space: normal !important;
}

/* li 공통 스타일
   - display: list-item → 불릿/숫자 기호가 제대로 렌더링되도록 강제
   - margin/padding 최소화로 라인 간격만 유지
   - white-space: normal → pre-wrap으로 인한 들여쓰기 깨짐 방지 */
:deep(.quill-content li) {
  display: list-item !important;
  margin: 0 0 !important;
  padding: 0 !important;
  white-space: normal !important;
  line-height: 1.6;
}

/* li 안의 p 복원
   - TinyMCE/Quill은 li 내부에 <p>를 생성하기도 함 (li > p 구조)
   - display: inline-block → 불릿과 텍스트가 같은 줄에 오도록
   - white-space: normal → 줄바꿈 시 불필요한 들여쓰기 방지 */
:deep(.quill-content li p) {
  display: inline-block !important;
  margin: 0 !important;
  padding: 0 !important;
  line-height: 1.6;
  white-space: normal !important;
}

/* 활동 수정 섹션 하이라이트 */
.edit-activity-section-highlighted {
  position: relative;
  background: #e5f6ff;
  border: 2px solid #0084ff;
  border-radius: 8px;
  padding: 16px;
  margin: 8px 0;
  box-shadow: 0 4px 12px rgba(0, 132, 255, 0.2);
  transform: translateY(-2px);
  transition: all 0.3s ease;

  &::after {
    content: v-bind(highlightEditingActivity);
    position: absolute;
    top: -8px;
    left: 12px;
    background: #0084ff;
    color: white;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    z-index: 2;
    box-shadow: 0 2px 4px rgba(0, 132, 255, 0.3);
  }

  &:hover {
    box-shadow: 0 6px 16px rgba(0, 132, 255, 0.25);
    transform: translateY(-3px);
  }
}

.comment-compose-footer {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #eee;
}

.attachment-btn.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border: none;
  background: transparent;
  color: #374151;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    color: #0084ff;
  }
}
</style>
